import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";

import type { ExhibitDocument } from "@upclaim/sdk/src/data-contracts";

import type { PreviewFile } from "@/components/files/file-preview-dialog";
import { queryKeys } from "@/lib/query-keys";
import adminAPI from "@/services/api";

import { groupExhibitDocuments, sortExhibitDocuments } from "./exhibit-utils";

/**
 * Fetch and shape the exhibit list for a claim, plus a file-preview helper —
 * port of composables/use-claim-exhibits.ts (Vue).
 *
 * Used by GenerateStampedExhibitsCTA (per-piece stamped folder flow),
 * MergeAssignationCTA (single Assignation.pdf flow) and GeneratePleadingBriefCTA.
 *
 * On top of the Vue composable, the ordered list is editable: items can be
 * moved up/down or removed before generation, and reset back to the server order.
 */
export function useClaimExhibits(claimId: string) {
  const query = useQuery({
    queryKey: [...queryKeys.claims.detail(claimId), "exhibits-documents"],
    queryFn: () => adminAPI.getExhibitsDocuments(claimId),
  });

  const serverExhibits = useMemo(() => sortExhibitDocuments(query.data?.documents ?? []), [query.data]);

  // Manual ordering layer — null means "follow the server order".
  const [orderedExhibits, setOrderedExhibits] = useState<ExhibitDocument[] | null>(null);
  const [previewedFile, setPreviewedFile] = useState<PreviewFile | null>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: reset the manual order when switching claim
  useEffect(() => {
    setOrderedExhibits(null);
    setPreviewedFile(null);
  }, [claimId]);

  const exhibits = orderedExhibits ?? serverExhibits;
  const isReordered = orderedExhibits !== null;

  const exhibitData = useMemo(() => groupExhibitDocuments(exhibits, isReordered), [exhibits, isReordered]);

  function moveExhibit(boxFileId: string, direction: "up" | "down") {
    const current = [...exhibits];
    const index = current.findIndex((doc) => doc.boxFileId === boxFileId);
    const target = direction === "up" ? index - 1 : index + 1;
    if (index < 0 || target < 0 || target >= current.length) {
      return;
    }
    [current[index], current[target]] = [current[target], current[index]];
    setOrderedExhibits(current);
  }

  function removeExhibit(boxFileId: string) {
    setOrderedExhibits(exhibits.filter((doc) => doc.boxFileId !== boxFileId));
  }

  function resetOrder() {
    setOrderedExhibits(null);
  }

  function previewDocument(file: { id: string; name: string } | null) {
    if (!file) {
      return;
    }
    setPreviewedFile({ id: file.id, name: file.name, mimeType: "application/pdf", size: null });
  }

  function clearPreview() {
    setPreviewedFile(null);
  }

  return {
    exhibits,
    exhibitData,
    isFetching: query.isPending,
    isReordered,
    moveExhibit,
    removeExhibit,
    resetOrder,
    previewedFile,
    previewDocument,
    clearPreview,
    refetchExhibits: query.refetch,
  };
}
